import { axiosInstance } from "./axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import {
  ICouncilAgendaItem,
  ICouncilArchiveYear,
  ICouncilAttachmentDownload,
  ICouncilGuest,
  ICouncilSession,
  ISaveCouncilAgendaRequest,
  ISaveCouncilAgendaResponse,
  ISaveCouncilGuestRequest,
  ISaveCouncilSessionRequest,
  ICouncilLegalActOption,
} from "dto/Council/ICouncil";

// Biuro rady — sesje rady, porządek obrad, załączniki punktów i goście.
// Załączniki porządku obrad są NIESZYFROWANE (mają być publicznie dostępne na
// portalu radnego) — upload idzie zwykłym /file/init|part|confirm bez KeyId,
// a BE oznacza plik jako jawny przy zapisie porządku.

export const councilQueryKeys = {
  sessions: "council.sessions",
  session: "council.session",
  agenda: "council.agenda",
  guests: "council.guests",
  archiveYears: "council.archiveYears",
  legalActOptions: "council.legalActOptions",
};

// ── Sesje ──────────────────────────────────────────────────────────────────

/**
 * @param archived undefined → wszystkie sesje (kalendarz archiwum),
 *                 false → bieżące (widok „Sesja"), true → archiwalne.
 */
const getSessions = (params: {
  archived?: boolean;
  year?: number;
}): Promise<{ data: ICouncilSession[] }> => {
  const search = new URLSearchParams();
  if (params.archived !== undefined) search.set("archived", String(params.archived));
  if (params.year !== undefined) search.set("year", String(params.year));

  const query = search.toString();

  return axiosInstance.get(`council/sessions${query ? `?${query}` : ""}`);
};

const useGetSessions = (params: { archived?: boolean; year?: number }) =>
  useCustomQuery([councilQueryKeys.sessions, params.archived, params.year], () =>
    getSessions(params),
  );

const getSession = (id: string): Promise<{ data: ICouncilSession }> =>
  axiosInstance.get(`council/sessions/${id}`);

const useGetSession = (id?: string) =>
  useCustomQuery(
    [councilQueryKeys.session, id],
    () => getSession(id as string),
    { enabled: !!id },
  );

const saveSession = (
  payload: ISaveCouncilSessionRequest,
): Promise<{ data: ICouncilSession }> =>
  axiosInstance.post("council/sessions", payload);

const useSaveSession = () => useCustomMutation(saveSession);

const publishSession = (id: string): Promise<{ data: ICouncilSession }> =>
  axiosInstance.post(`council/sessions/${id}/publish`);

const usePublishSession = () => useCustomMutation(publishSession);

const archiveSession = (id: string): Promise<{ data: unknown }> =>
  axiosInstance.post(`council/sessions/${id}/archive`);

const useArchiveSession = () => useCustomMutation(archiveSession);

const restoreSession = (id: string): Promise<{ data: unknown }> =>
  axiosInstance.post(`council/sessions/${id}/restore`);

const useRestoreSession = () => useCustomMutation(restoreSession);

const removeSession = (id: string): Promise<{ data: unknown }> =>
  axiosInstance.delete(`council/sessions/${id}`);

const useRemoveSession = () => useCustomMutation(removeSession);

const getArchiveYears = (): Promise<{ data: ICouncilArchiveYear[] }> =>
  axiosInstance.get("council/archive/years");

const useGetArchiveYears = () =>
  useCustomQuery(councilQueryKeys.archiveYears, () => getArchiveYears());

// ── Porządek obrad ─────────────────────────────────────────────────────────

const getAgenda = (sessionId: string): Promise<{ data: ICouncilAgendaItem[] }> =>
  axiosInstance.get(`council/sessions/${sessionId}/agenda`);

const useGetAgenda = (sessionId?: string) =>
  useCustomQuery(
    [councilQueryKeys.agenda, sessionId],
    () => getAgenda(sessionId as string),
    { enabled: !!sessionId },
  );

const saveAgenda = (payload: {
  sessionId: string;
  request: ISaveCouncilAgendaRequest;
}): Promise<{ data: ISaveCouncilAgendaResponse }> =>
  axiosInstance.post(`council/sessions/${payload.sessionId}/agenda`, payload.request);

const useSaveAgenda = () => useCustomMutation(saveAgenda);

/**
 * Projekty aktów, które można dołączyć do punktu porządku obrad jako relację.
 * Odpowiednik listy „do przyjęcia" z modala załączników w Magistracie.
 */
const getLegalActOptions = (
  sessionId?: string,
): Promise<{ data: ICouncilLegalActOption[] }> =>
  axiosInstance.get(
    `council/legalacts/options${sessionId ? `?sessionId=${sessionId}` : ""}`,
  );

const useGetLegalActOptions = (sessionId?: string, enabled = true) =>
  useCustomQuery(
    [councilQueryKeys.legalActOptions, sessionId],
    () => getLegalActOptions(sessionId),
    { enabled },
  );

// ── Załączniki ─────────────────────────────────────────────────────────────

const getAttachmentDownload = (
  attachmentId: string,
): Promise<{ data: ICouncilAttachmentDownload }> =>
  axiosInstance.get(`council/attachments/${attachmentId}/download`);

const useGetAttachmentDownload = () => useCustomMutation(getAttachmentDownload);

/** Publiczny (anonimowy) odnośnik do pliku — działa dopiero po opublikowaniu sesji. */
export const getPublicAttachmentUrl = (attachmentId: string) =>
  `${axiosInstance.defaults.baseURL}/council/public/attachments/${attachmentId}/content`;

// ── Goście ─────────────────────────────────────────────────────────────────

const getGuests = (sessionId: string): Promise<{ data: ICouncilGuest[] }> =>
  axiosInstance.get(`council/sessions/${sessionId}/guests`);

const useGetGuests = (sessionId?: string) =>
  useCustomQuery(
    [councilQueryKeys.guests, sessionId],
    () => getGuests(sessionId as string),
    { enabled: !!sessionId },
  );

const saveGuest = (payload: {
  sessionId: string;
  request: ISaveCouncilGuestRequest;
}): Promise<{ data: ICouncilGuest }> =>
  axiosInstance.post(`council/sessions/${payload.sessionId}/guests`, payload.request);

const useSaveGuest = () => useCustomMutation(saveGuest);

const removeGuest = (guestId: string): Promise<{ data: unknown }> =>
  axiosInstance.delete(`council/guests/${guestId}`);

const useRemoveGuest = () => useCustomMutation(removeGuest);

export default {
  useGetSessions,
  useGetSession,
  useSaveSession,
  usePublishSession,
  useArchiveSession,
  useRestoreSession,
  useRemoveSession,
  useGetArchiveYears,
  useGetAgenda,
  useSaveAgenda,
  getLegalActOptions,
  useGetLegalActOptions,
  useGetAttachmentDownload,
  useGetGuests,
  useSaveGuest,
  useRemoveGuest,
};
